import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, ChevronRight } from "lucide-react";

//🔥 Variants globales para animaciones
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

// const fade = {
//   hidden: { opacity: 0 },
//   show: { opacity: 1, transition: { duration: 0.7 } },
// };

export default function ServicesPage() {
  return (
    <div className="bg-gray-950 text-white w-full min-h-screen overflow-hidden">

      {/* 🔹 BREADCRUMBS */}
      <div className="bg-gray-900/60 py-3 px-6 text-sm flex items-center space-x-2 border-b border-gray-800">
        <span className="text-gray-400">Home</span>
        <ChevronRight className="w-4 h-4 text-gray-500" />
        <span className="font-semibold text-gray-100">Services</span>
      </div>

      {/* 🔹 MINI HERO */}
      <section className="relative w-full h-[50vh] flex items-center justify-center overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute w-full h-full object-cover opacity-50"
          src="https://cdn.coverr.co/videos/coverr-clouds-moving-over-mountains-3425/1080p.mp4"
        />

        {/* Glow lights */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/70 via-black/70 to-gray-950/90"></div>
        <div className="absolute -top-20 left-20 w-96 h-96 bg-cyan-500/20 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 right-10 w-80 h-80 bg-blue-600/20 blur-3xl rounded-full"></div>

        <motion.h1
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="relative z-20 text-4xl md:text-6xl font-bold text-center"
        >
          Our <span className="text-cyan-400">Professional Services</span>
        </motion.h1>
      </section>

      {/* 🔹 WHY CHOOSE US */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          className="text-3xl md:text-4xl font-bold text-center mb-12"
        >
          Why Clients Choose Kaizen
        </motion.h2>

        <div className="grid md:grid-cols-3 gap-10">
          {[
            {
              title: "Top-Quality Work",
              desc: "We deliver premium solutions with precision, performance, and excellence.",
            },
            {
              title: "Fast & Efficient",
              desc: "We optimize development time without compromising on quality.",
            },
            {
              title: "Trusted Worldwide",
              desc: "Businesses across industries trust our expertise and long-term results.",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              className="p-7 bg-gray-900/60 border border-gray-800 rounded-2xl shadow-xl
              hover:shadow-cyan-500/20 hover:-translate-y-1 transition-all duration-300 backdrop-blur"
            >
              <CheckCircle className="text-cyan-400 w-12 h-12 mb-4" />
              <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
              <p className="text-gray-400">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 🔹 PROCESS SECTION */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-950 to-gray-900">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          className="text-3xl md:text-4xl font-bold text-center mb-14"
        >
          How We Work
        </motion.h2>

        <div className="grid md:grid-cols-4 gap-10 max-w-6xl mx-auto">
          {[
            { step: "1. Consultation", desc: "We deeply understand your goals and needs." },
            { step: "2. Planning", desc: "We design a strategic roadmap for success." },
            { step: "3. Execution", desc: "We deliver high-quality work with precision." },
            { step: "4. Delivery", desc: "Final delivery with full satisfaction and support." },
          ].map((item, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              className="p-8 bg-gray-900/50 border border-gray-800 rounded-xl shadow-lg hover:shadow-cyan-500/20 transition"
            >
              <h3 className="text-xl font-bold text-cyan-400 mb-3">{item.step}</h3>
              <p className="text-gray-400">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 🔹 PRICING CARDS */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          className="text-3xl md:text-4xl font-bold text-center mb-12"
        >
          Our Service Packages
        </motion.h2>

        <div className="grid md:grid-cols-3 gap-10">
          {[
            {
              name: "Basic Package",
              list: ["Standard Services", "Email Support", "Fast Delivery"],
            },
            {
              name: "Professional Package",
              list: ["Advanced Services", "Priority Support", "Extended Warranty"],
            },
            {
              name: "Enterprise Package",
              list: ["Custom Solutions", "Dedicated Support", "Full Integration"],
            },
          ].map((plan, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              className="p-10 bg-gray-900/60 border border-gray-800 rounded-2xl shadow-xl hover:shadow-cyan-500/30 
              hover:-translate-y-1 transition-all duration-300"
            >
              <h3 className="text-2xl font-bold text-center mb-6 text-cyan-400">{plan.name}</h3>

              <ul className="space-y-3 mb-6 text-gray-300">
                {plan.list.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-cyan-400" />
                    {item}
                  </li>
                ))}
              </ul>

              <button className="w-full py-3 bg-cyan-500 text-gray-900 rounded-xl font-semibold hover:bg-cyan-400 transition flex items-center justify-center gap-2">
                Request a Quote <ArrowRight size={18} />
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 🔹 FAQ SECTION */}
      <section className="py-20 bg-gray-900 px-6">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          className="text-3xl md:text-4xl font-bold text-center mb-10"
        >
          Frequently Asked Questions
        </motion.h2>

        <div className="max-w-3xl mx-auto space-y-6">
          {[
            {
              q: "How long does a service project take?",
              a: "Most projects are completed within a few days, depending on scope.",
            },
            {
              q: "Do you offer custom solutions?",
              a: "Yes — all services can be tailored to your specific needs.",
            },
            {
              q: "Is support included?",
              a: "Absolutely. All packages include before & after delivery support.",
            },
          ].map((faq, i) => (
            <motion.details
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              className="bg-gray-950 p-6 rounded-xl border border-gray-800 shadow"
            >
              <summary className="cursor-pointer font-semibold text-lg text-cyan-400">
                {faq.q}
              </summary>
              <p className="text-gray-400 mt-3">{faq.a}</p>
            </motion.details>
          ))}
        </div>
      </section>
    </div>
  );
}